import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { capabilities } from '../data/capabilities';
import { Breadcrumbs } from '../components/Breadcrumbs';
export function CapabilityPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const capability = capabilities.find(c => c.slug === slug);
  if (!capability) {
    return <Navigate to="/capabilities" replace data-id="element-377" />;
  }
  return <>
      <PageHero label="Capability Profile" title={capability.name} subtitle={capability.shortDescription} data-id="element-378" />

      <Breadcrumbs items={[{
      label: 'Manufacturing',
      to: '/manufacturing'
    }, {
      label: 'Our Processes',
      to: '/manufacturing/processes'
    }, {
      label: capability.name
    }]} data-id="element-379" />

      {/* Overview */}
      <section className="bg-white py-24" data-id="element-380">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-381">
          <div className="max-w-3xl" data-id="element-382">
            <h2 className="text-2xl font-bold text-navy-900 mb-6" data-id="element-383">Overview</h2>
            <p className="text-lg text-steel-600 leading-relaxed mb-12" data-id="element-384">
              {capability.description}
            </p>
          </div>

          {/* How It Works - Dossier Style */}
          <h3 className="text-xl font-bold text-navy-900 mb-8" data-id="element-385">
            Process Workflow
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-16" data-id="element-386">
            {capability.howItWorks.map(step => <div key={step.step} className="bg-white p-6 border border-steel-200 border-t-4 border-t-cg-orange-600" data-id="element-387">
                <span className="text-4xl font-black text-steel-200 mb-4 block" data-id="element-388">
                  0{step.step}
                </span>
                <h4 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-2" data-id="element-389">
                  {step.title}
                </h4>
                <p className="text-sm text-steel-600" data-id="element-390">{step.description}</p>
              </div>)}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-391">
            {/* Typical Outputs */}
            <div data-id="element-392">
              <h3 className="text-xl font-bold text-navy-900 mb-6" data-id="element-393">
                Typical Outputs
              </h3>
              <ul className="space-y-4" data-id="element-394">
                {capability.typicalOutputs.map((output, i) => <li key={i} className="flex items-center gap-3 p-4 bg-slate-50 border border-steel-100 rounded-sm" data-id="element-395">
                    <CheckCircle2 className="w-5 h-5 text-cg-orange-600" data-id="element-396" />
                    <span className="text-steel-700 font-medium" data-id="element-397">{output}</span>
                  </li>)}
              </ul>
            </div>

            {/* Supported Sectors */}
            <div data-id="element-398">
              <h3 className="text-xl font-bold text-navy-900 mb-6" data-id="element-399">
                Supported Sectors
              </h3>
              <div className="flex flex-wrap gap-3" data-id="element-400">
                {capability.supportedSectors.map(sectorSlug => <Link key={sectorSlug} to={`/sectors/${sectorSlug}`} className="px-4 py-2 bg-navy-900 text-white text-sm font-bold uppercase tracking-wider hover:bg-cg-orange-600 transition-colors rounded-sm" data-id="element-401">
                    {sectorSlug.charAt(0).toUpperCase() + sectorSlug.slice(1)}
                  </Link>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy-900 py-20" data-id="element-402">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center" data-id="element-403">
          <h2 className="text-3xl font-bold text-white mb-6" data-id="element-404">
            Discuss a Requirement
          </h2>
          <p className="text-steel-300 max-w-2xl mx-auto mb-10 text-lg" data-id="element-405">
            Leverage our {capability.name.toLowerCase()} capabilities for your
            next procurement project.
          </p>
          <Link to="/contact" className="inline-flex items-center justify-center px-10 py-4 bg-cg-orange-600 hover:bg-cg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm transition-colors" data-id="element-406">
            Contact Us
          </Link>
        </div>
      </section>
    </>;
}